import { useState, useEffect, useRef } from 'react';
import { useParams, useNavigate } from 'react-router-dom';
import { adminApi } from '../services/api';
import toast from 'react-hot-toast';
import { Dialog } from '@headlessui/react';
import { format, formatDistanceToNow } from 'date-fns';
import {
  ArrowLeftIcon,
  PaperAirplaneIcon,
  CheckCircleIcon,
  ClockIcon,
  ChatBubbleLeftIcon,
  ExclamationTriangleIcon,
  XMarkIcon,
  UserIcon,
  ShieldCheckIcon,
  CogIcon,
  SparklesIcon,
  LinkIcon,
  BugAntIcon,
  ArrowPathIcon,
  LightBulbIcon,
  ChatBubbleBottomCenterTextIcon,
  ChevronDownIcon,
  ChevronUpIcon
} from '@heroicons/react/24/outline';

const STATUS_CONFIG = {
  open: { label: 'Open', color: 'badge-danger' },
  in_progress: { label: 'In Progress', color: 'badge-warning' },
  awaiting_user: { label: 'Awaiting User', color: 'badge-info' },
  resolved: { label: 'Resolved', color: 'badge-success' },
  closed: { label: 'Closed', color: 'badge-neutral' }
};

const PRIORITY_CONFIG = {
  low: { label: 'Low', color: 'text-surface-400' },
  medium: { label: 'Medium', color: 'text-blue-400' },
  high: { label: 'High', color: 'text-amber-400' },
  urgent: { label: 'Urgent', color: 'text-red-400' }
};

export default function TicketDetail() {
  const { id } = useParams();
  const navigate = useNavigate();
  const messagesEndRef = useRef(null);
  
  const [ticket, setTicket] = useState(null);
  const [messages, setMessages] = useState([]);
  const [loading, setLoading] = useState(true);
  const [newMessage, setNewMessage] = useState('');
  const [sending, setSending] = useState(false);
  
  // AI features
  const [generatingResponse, setGeneratingResponse] = useState(false);
  const [matchingIssues, setMatchingIssues] = useState(false);
  const [matchedIssues, setMatchedIssues] = useState([]);
  
  // AI Analysis
  const [analyzingTicket, setAnalyzingTicket] = useState(false);
  const [ticketAnalysis, setTicketAnalysis] = useState(null);
  const [analysisExpanded, setAnalysisExpanded] = useState(true);
  
  // AI Response Assistant Modal
  const [responseAssistantOpen, setResponseAssistantOpen] = useState(false);
  const [responseContext, setResponseContext] = useState({
    stepsTaken: '',
    resolution: '',
    additionalInfo: '',
    tone: 'professional'
  });
  const [generatingGuidedResponse, setGeneratingGuidedResponse] = useState(false);
  
  // Status update
  const [updatingStatus, setUpdatingStatus] = useState(false);
  
  useEffect(() => {
    loadTicket();
  }, [id]);
  
  useEffect(() => {
    messagesEndRef.current?.scrollIntoView({ behavior: 'smooth' });
  }, [messages]);
  
  const loadTicket = async () => {
    try {
      setLoading(true);
      const response = await adminApi.getTicket(id);
      setTicket(response.data.ticket);
      setMessages(response.data.messages);
    } catch (error) {
      toast.error('Failed to load ticket');
      navigate('/tickets');
    } finally {
      setLoading(false);
    }
  };
  
  const handleSendMessage = async (e, isAIGenerated = false) => {
    e?.preventDefault();
    if (!newMessage.trim() || sending) return;
    
    setSending(true);
    try {
      const response = await adminApi.addTicketMessage(id, newMessage, isAIGenerated);
      setMessages(prev => [...prev, response.data.message]);
      setNewMessage('');
      // Reload ticket for updated status
      const ticketResponse = await adminApi.getTicket(id);
      setTicket(ticketResponse.data.ticket);
    } catch (error) {
      toast.error('Failed to send message');
    } finally {
      setSending(false);
    }
  };
  
  const handleStatusChange = async (status) => {
    setUpdatingStatus(true);
    try {
      await adminApi.updateTicket(id, { status });
      toast.success('Status updated');
      loadTicket();
    } catch (error) {
      toast.error('Failed to update status');
    } finally {
      setUpdatingStatus(false);
    }
  };
  
  const handleCloseTicket = async () => {
    if (!window.confirm('Close this ticket?')) return;
    
    try {
      await adminApi.closeTicket(id);
      toast.success('Ticket closed');
      loadTicket();
    } catch (error) {
      toast.error('Failed to close ticket');
    }
  };
  
  const handleGenerateAIResponse = async () => {
    setGeneratingResponse(true);
    try {
      const response = await adminApi.generateAIResponse(id);
      setNewMessage(response.data.suggestedResponse);
      toast.success('AI response generated - review before sending');
    } catch (error) {
      toast.error('Failed to generate AI response');
    } finally {
      setGeneratingResponse(false);
    }
  };
  
  const handleMatchIssues = async () => {
    setMatchingIssues(true);
    try {
      const response = await adminApi.matchTicketWithIssues(id);
      setMatchedIssues(response.data.matches);
      if (response.data.matches.length === 0) {
        toast('No matching issues found', { icon: 'ℹ️' });
      } else {
        toast.success(`Found ${response.data.matches.length} related issues`);
      }
    } catch (error) {
      toast.error('Failed to match issues');
    } finally {
      setMatchingIssues(false);
    }
  };
  
  const handleLinkIssue = async (issueId) => {
    try {
      await adminApi.updateTicket(id, { linkedIssueId: issueId });
      toast.success('Issue linked to ticket');
      loadTicket();
    } catch (error) {
      toast.error('Failed to link issue');
    }
  };
  
  const handleAnalyzeTicket = async () => {
    setAnalyzingTicket(true);
    try {
      const response = await adminApi.analyzeTicket(id);
      setTicketAnalysis(response.data.analysis);
      setAnalysisExpanded(true);
    } catch (error) {
      toast.error('Failed to analyze ticket');
    } finally {
      setAnalyzingTicket(false);
    }
  };
  
  const handleGenerateGuidedResponse = async () => {
    setGeneratingGuidedResponse(true);
    try {
      const response = await adminApi.generateGuidedResponse(id, responseContext);
      setNewMessage(response.data.suggestedResponse);
      setResponseAssistantOpen(false);
      setResponseContext({ stepsTaken: '', resolution: '', additionalInfo: '', tone: 'professional' });
      toast.success('Response generated - review before sending');
    } catch (error) {
      toast.error('Failed to generate response');
    } finally {
      setGeneratingGuidedResponse(false);
    }
  };
  
  if (loading) {
    return (
      <div className="flex items-center justify-center min-h-[400px]">
        <div className="w-8 h-8 border-4 border-primary-600 border-t-transparent rounded-full animate-spin" />
      </div>
    );
  }
  
  if (!ticket) {
    return (
      <div className="text-center py-12">
        <p className="text-surface-400">Ticket not found</p>
        <button onClick={() => navigate('/tickets')} className="btn-primary mt-4">
          Back to Tickets
        </button>
      </div>
    );
  }
  
  const isClosed = ['closed', 'resolved'].includes(ticket.status);
  
  return (
    <div className="space-y-6 animate-fade-in">
      {/* Header */}
      <div className="flex items-start justify-between">
        <div className="flex items-start gap-4">
          <button
            onClick={() => navigate('/tickets')}
            className="p-2 hover:bg-surface-800 rounded-lg mt-1"
          >
            <ArrowLeftIcon className="w-5 h-5 text-surface-400" />
          </button>
          <div>
            <h1 className="text-xl font-bold text-surface-100 mb-2">
              {ticket.subject}
            </h1>
            <div className="flex items-center gap-2 text-sm">
              <span className="text-surface-500">
                #{ticket._id.slice(-6).toUpperCase()}
              </span>
              <span className={STATUS_CONFIG[ticket.status]?.color}>
                {STATUS_CONFIG[ticket.status]?.label}
              </span>
              <span className={PRIORITY_CONFIG[ticket.priority]?.color}>
                {PRIORITY_CONFIG[ticket.priority]?.label} Priority
              </span>
            </div>
          </div>
        </div>
      </div>
      
      <div className="grid lg:grid-cols-3 gap-6">
        {/* Main Content - Messages */}
        <div className="lg:col-span-2 space-y-4">
          {/* Technical Details */}
          {ticket.errorDetails?.errorMessage && (
            <div className="card bg-red-900/20 border-red-800">
              <h3 className="font-medium text-red-400 mb-2 flex items-center gap-2">
                <ExclamationTriangleIcon className="w-5 h-5" />
                Error Details
              </h3>
              <pre className="text-sm text-red-300 font-mono whitespace-pre-wrap bg-red-900/30 p-3 rounded-lg overflow-x-auto">
                {ticket.errorDetails.errorMessage}
              </pre>
              {ticket.errorDetails.stepsToReproduce && (
                <div className="mt-3 pt-3 border-t border-red-800">
                  <p className="text-sm font-medium text-red-400 mb-1">Steps to Reproduce:</p>
                  <p className="text-sm text-red-300 whitespace-pre-wrap">
                    {ticket.errorDetails.stepsToReproduce}
                  </p>
                </div>
              )}
            </div>
          )}
          
          {/* AI Analysis Panel */}
          <div className="card bg-purple-900/10 border-purple-800/30">
            <div className="flex items-center justify-between mb-2">
              <h3 className="font-medium text-purple-300 flex items-center gap-2">
                <LightBulbIcon className="w-5 h-5" />
                AI Issue Analysis
              </h3>
              <div className="flex items-center gap-2">
                {ticketAnalysis && (
                  <button
                    onClick={() => setAnalysisExpanded(!analysisExpanded)}
                    className="p-1 hover:bg-purple-800/30 rounded"
                  >
                    {analysisExpanded ? (
                      <ChevronUpIcon className="w-4 h-4 text-purple-400" />
                    ) : (
                      <ChevronDownIcon className="w-4 h-4 text-purple-400" />
                    )}
                  </button>
                )}
                <button
                  onClick={handleAnalyzeTicket}
                  disabled={analyzingTicket}
                  className="btn-ghost text-sm text-purple-300 hover:bg-purple-800/30"
                >
                  {analyzingTicket ? (
                    <>
                      <ArrowPathIcon className="w-4 h-4 animate-spin" />
                      Analyzing...
                    </>
                  ) : ticketAnalysis ? (
                    <>
                      <ArrowPathIcon className="w-4 h-4" />
                      Re-analyze
                    </>
                  ) : (
                    <>
                      <SparklesIcon className="w-4 h-4" />
                      Analyze Issue
                    </>
                  )}
                </button>
              </div>
            </div>
            
            {!ticketAnalysis && !analyzingTicket && (
              <p className="text-sm text-purple-400/70">
                Click "Analyze Issue" to get AI-powered insights about this ticket, including issue summary, likely causes, and recommended next steps.
              </p>
            )}
            
            {analyzingTicket && (
              <div className="flex items-center justify-center py-8">
                <div className="text-center">
                  <ArrowPathIcon className="w-8 h-8 text-purple-400 animate-spin mx-auto mb-2" />
                  <p className="text-sm text-purple-400">Analyzing ticket...</p>
                </div>
              </div>
            )}
            
            {ticketAnalysis && analysisExpanded && (
              <div className="prose prose-sm prose-invert max-w-none mt-3">
                <div className="bg-purple-900/20 rounded-lg p-4 text-sm text-purple-100 whitespace-pre-wrap">
                  {ticketAnalysis}
                </div>
              </div>
            )}
          </div>
          
          {/* Messages */}
          <div className="card">
            <h3 className="font-semibold text-surface-100 mb-4">Conversation</h3>
            
            <div className="space-y-4 max-h-[500px] overflow-y-auto mb-4">
              {messages.map((message, index) => {
                const isUser = message.senderType === 'user';
                const isSystem = message.senderType === 'system';
                
                if (isSystem) {
                  return (
                    <div key={message._id || index} className="flex justify-center">
                      <div className="flex items-center gap-2 text-xs text-surface-500 bg-surface-800 px-3 py-1.5 rounded-full">
                        <CogIcon className="w-3.5 h-3.5" />
                        {message.content}
                      </div>
                    </div>
                  );
                }
                
                return (
                  <div
                    key={message._id || index}
                    className={`flex ${isUser ? 'justify-start' : 'justify-end'}`}
                  >
                    <div className="max-w-[80%]">
                      <div className="flex items-center gap-2 mb-1">
                        {isUser ? (
                          <UserIcon className="w-4 h-4 text-surface-400" />
                        ) : (
                          <ShieldCheckIcon className="w-4 h-4 text-primary-400" />
                        )}
                        <span className="text-xs font-medium text-surface-400">
                          {isUser 
                            ? `${message.senderId?.firstName || 'User'} ${message.senderId?.lastName || ''}`
                            : `${message.senderId?.firstName || 'Admin'}`
                          }
                        </span>
                        {message.isAIGenerated && (
                          <SparklesIcon className="w-3.5 h-3.5 text-purple-400" title="AI-generated" />
                        )}
                        <span className="text-xs text-surface-500">
                          {format(new Date(message.createdAt), 'MMM d, h:mm a')}
                        </span>
                      </div>
                      
                      <div
                        className={`p-3 rounded-lg ${
                          isUser
                            ? 'bg-surface-800 text-surface-200'
                            : 'bg-primary-600 text-white'
                        }`}
                      >
                        <p className="whitespace-pre-wrap">{message.content}</p>
                      </div>
                    </div>
                  </div>
                );
              })}
              <div ref={messagesEndRef} />
            </div>
            
            {/* Reply Input */}
            {!isClosed ? (
              <div className="border-t border-surface-700 pt-4">
                <div className="flex flex-wrap gap-2 mb-3">
                  <button
                    onClick={() => setResponseAssistantOpen(true)}
                    className="btn-ghost text-sm text-purple-400 hover:bg-purple-900/20 border border-purple-500/30"
                  >
                    <ChatBubbleBottomCenterTextIcon className="w-4 h-4" />
                    Response Assistant
                  </button>
                  <button
                    onClick={handleGenerateAIResponse}
                    disabled={generatingResponse}
                    className="btn-ghost text-sm text-surface-400 hover:bg-surface-700"
                  >
                    {generatingResponse ? (
                      <ArrowPathIcon className="w-4 h-4 animate-spin" />
                    ) : (
                      <SparklesIcon className="w-4 h-4" />
                    )}
                    Quick AI Response
                  </button>
                </div>
                
                <form onSubmit={handleSendMessage} className="flex gap-3">
                  <textarea
                    value={newMessage}
                    onChange={(e) => setNewMessage(e.target.value)}
                    placeholder="Type your response..."
                    className="input flex-1 resize-none h-20"
                  />
                  <button
                    type="submit"
                    disabled={sending || !newMessage.trim()}
                    className="btn-primary self-end"
                  >
                    {sending ? (
                      <ArrowPathIcon className="w-5 h-5 animate-spin" />
                    ) : (
                      <PaperAirplaneIcon className="w-5 h-5" />
                    )}
                  </button>
                </form>
              </div>
            ) : (
              <div className="border-t border-surface-700 pt-4 text-center text-surface-500">
                This ticket is closed
              </div>
            )}
          </div>
        </div>
        
        {/* Sidebar */}
        <div className="space-y-4">
          {/* User Info */}
          <div className="card">
            <h3 className="font-semibold text-surface-100 mb-4">Customer</h3>
            <div className="flex items-center gap-3 mb-3">
              <div className="w-10 h-10 rounded-full bg-surface-700 flex items-center justify-center text-surface-300">
                {ticket.userId?.firstName?.[0]}{ticket.userId?.lastName?.[0]}
              </div>
              <div>
                <p className="font-medium text-surface-100">
                  {ticket.userId?.firstName} {ticket.userId?.lastName}
                </p>
                <p className="text-sm text-surface-400">{ticket.userId?.email}</p>
              </div>
            </div>
            <div className="text-sm text-surface-400">
              <p>Company: {ticket.companyId?.name}</p>
              <p>Role: {ticket.userId?.role}</p>
            </div>
          </div>
          
          {/* Status Management */}
          <div className="card">
            <h3 className="font-semibold text-surface-100 mb-4">Manage Ticket</h3>
            
            <div className="space-y-3">
              <div>
                <label className="text-xs text-surface-400 mb-1 block">Status</label>
                <select
                  value={ticket.status}
                  onChange={(e) => handleStatusChange(e.target.value)}
                  disabled={updatingStatus}
                  className="input w-full"
                >
                  {Object.entries(STATUS_CONFIG).map(([value, { label }]) => (
                    <option key={value} value={value}>{label}</option>
                  ))}
                </select>
              </div>
              
              {!isClosed && (
                <button
                  onClick={handleCloseTicket}
                  className="btn-ghost w-full text-red-400 hover:bg-red-900/20"
                >
                  <XMarkIcon className="w-4 h-4" />
                  Close Ticket
                </button>
              )}
            </div>
          </div>
          
          {/* AI Issue Matching */}
          <div className="card">
            <h3 className="font-semibold text-surface-100 mb-4 flex items-center gap-2">
              <BugAntIcon className="w-5 h-5 text-purple-400" />
              Issue Matching
            </h3>
            
            {ticket.linkedIssueId ? (
              <div className="bg-emerald-900/20 border border-emerald-800 rounded-lg p-3 mb-3">
                <p className="text-xs text-emerald-400 mb-1">Linked Issue:</p>
                <p className="text-sm text-emerald-300">{ticket.linkedIssueId.title}</p>
                <button
                  onClick={() => navigate(`/issues?id=${ticket.linkedIssueId._id}`)}
                  className="text-xs text-emerald-400 hover:underline mt-1"
                >
                  View Issue →
                </button>
              </div>
            ) : null}
            
            <button
              onClick={handleMatchIssues}
              disabled={matchingIssues}
              className="btn-secondary w-full"
            >
              {matchingIssues ? (
                <ArrowPathIcon className="w-4 h-4 animate-spin" />
              ) : (
                <SparklesIcon className="w-4 h-4" />
              )}
              Find Related Issues
            </button>
            
            {matchedIssues.length > 0 && (
              <div className="mt-4 space-y-2">
                <p className="text-xs text-surface-400">Matched Issues:</p>
                {matchedIssues.map((match) => (
                  <div
                    key={match.issueId}
                    className="bg-surface-800 rounded-lg p-3"
                  >
                    <div className="flex items-center justify-between mb-1">
                      <p className="text-sm font-medium text-surface-200 truncate">
                        {match.issue?.title}
                      </p>
                      <span className="text-xs text-purple-400">
                        {match.confidence}%
                      </span>
                    </div>
                    <p className="text-xs text-surface-400 mb-2">{match.reason}</p>
                    <button
                      onClick={() => handleLinkIssue(match.issueId)}
                      className="text-xs text-primary-400 hover:underline flex items-center gap-1"
                    >
                      <LinkIcon className="w-3 h-3" />
                      Link to Ticket
                    </button>
                  </div>
                ))}
              </div>
            )}
          </div>
          
          {/* Ticket Meta */}
          <div className="card">
            <h3 className="font-semibold text-surface-100 mb-4">Details</h3>
            <div className="space-y-2 text-sm">
              <div className="flex justify-between">
                <span className="text-surface-400">Created</span>
                <span className="text-surface-200">
                  {format(new Date(ticket.createdAt), 'MMM d, yyyy')}
                </span>
              </div>
              <div className="flex justify-between">
                <span className="text-surface-400">Category</span>
                <span className="text-surface-200 capitalize">
                  {ticket.category?.replace('_', ' ')}
                </span>
              </div>
              <div className="flex justify-between">
                <span className="text-surface-400">Messages</span>
                <span className="text-surface-200">{ticket.messageCount}</span>
              </div>
              {ticket.assignedTo && (
                <div className="flex justify-between">
                  <span className="text-surface-400">Assigned To</span>
                  <span className="text-surface-200">
                    {ticket.assignedTo.firstName}
                  </span>
                </div>
              )}
            </div>
          </div>
        </div>
      </div>
      
      {/* Response Assistant Modal */}
      <Dialog open={responseAssistantOpen} onClose={() => !generatingGuidedResponse && setResponseAssistantOpen(false)} className="relative z-50">
        <div className="fixed inset-0 bg-black/50" aria-hidden="true" />
        
        <div className="fixed inset-0 flex items-center justify-center p-4">
          <Dialog.Panel className="card w-full max-w-xl">
            <div className="flex items-center justify-between mb-6">
              <Dialog.Title className="text-lg font-semibold text-surface-100 flex items-center gap-2">
                <ChatBubbleBottomCenterTextIcon className="w-6 h-6 text-purple-400" />
                Response Assistant
              </Dialog.Title>
              {!generatingGuidedResponse && (
                <button onClick={() => setResponseAssistantOpen(false)} className="text-surface-400 hover:text-surface-100">
                  <XMarkIcon className="w-5 h-5" />
                </button>
              )}
            </div>
            
            <p className="text-sm text-surface-400 mb-4">
              Provide context about the issue and your actions. The AI will craft a personalized response based on this information.
            </p>
            
            <div className="space-y-4">
              <div>
                <label className="block text-sm font-medium text-surface-300 mb-1">
                  What steps have you taken? <span className="text-surface-500">(optional)</span>
                </label>
                <textarea
                  value={responseContext.stepsTaken}
                  onChange={(e) => setResponseContext(c => ({ ...c, stepsTaken: e.target.value }))}
                  placeholder="e.g., Checked the logs, reproduced the issue, applied a fix..."
                  className="input w-full h-24 resize-none"
                />
              </div>
              
              <div>
                <label className="block text-sm font-medium text-surface-300 mb-1">
                  Resolution or current status <span className="text-surface-500">(optional)</span>
                </label>
                <textarea
                  value={responseContext.resolution}
                  onChange={(e) => setResponseContext(c => ({ ...c, resolution: e.target.value }))}
                  placeholder="e.g., Issue has been fixed, still investigating, need more info from user..."
                  className="input w-full h-20 resize-none"
                />
              </div>
              
              <div>
                <label className="block text-sm font-medium text-surface-300 mb-1">
                  Additional information <span className="text-surface-500">(optional)</span>
                </label>
                <textarea
                  value={responseContext.additionalInfo}
                  onChange={(e) => setResponseContext(c => ({ ...c, additionalInfo: e.target.value }))}
                  placeholder="Any other context to include in the response..."
                  className="input w-full h-16 resize-none"
                />
              </div>
              
              <div>
                <label className="block text-sm font-medium text-surface-300 mb-1">
                  Tone
                </label>
                <div className="grid grid-cols-2 gap-2">
                  {[
                    { value: 'professional', label: 'Professional', desc: 'Formal and business-like' },
                    { value: 'friendly', label: 'Friendly', desc: 'Warm and conversational' },
                    { value: 'apologetic', label: 'Apologetic', desc: 'Start with sincere apology' },
                    { value: 'technical', label: 'Technical', desc: 'Detailed explanations' }
                  ].map(tone => (
                    <label
                      key={tone.value}
                      className={`flex items-start gap-2 p-3 rounded-lg cursor-pointer border transition-colors ${
                        responseContext.tone === tone.value
                          ? 'bg-purple-500/10 border-purple-500/50'
                          : 'bg-surface-800 border-surface-700 hover:border-surface-600'
                      }`}
                    >
                      <input
                        type="radio"
                        name="tone"
                        value={tone.value}
                        checked={responseContext.tone === tone.value}
                        onChange={(e) => setResponseContext(c => ({ ...c, tone: e.target.value }))}
                        className="mt-1"
                      />
                      <div>
                        <p className="text-sm font-medium text-surface-200">{tone.label}</p>
                        <p className="text-xs text-surface-500">{tone.desc}</p>
                      </div>
                    </label>
                  ))}
                </div>
              </div>
            </div>
            
            <div className="flex gap-3 pt-4 mt-4 border-t border-surface-700">
              <button
                onClick={() => setResponseAssistantOpen(false)}
                disabled={generatingGuidedResponse}
                className="btn-secondary flex-1"
              >
                Cancel
              </button>
              <button
                onClick={handleGenerateGuidedResponse}
                disabled={generatingGuidedResponse}
                className="btn-primary flex-1"
              >
                {generatingGuidedResponse ? (
                  <>
                    <ArrowPathIcon className="w-5 h-5 animate-spin" />
                    Generating...
                  </>
                ) : (
                  <>
                    <SparklesIcon className="w-5 h-5" />
                    Generate Response
                  </>
                )}
              </button>
            </div>
          </Dialog.Panel>
        </div>
      </Dialog>
    </div>
  );
}
